import { useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useGetWorkflowById } from '../../../../services/workflow.services';
import { useUserGroupsCurrentUserAdminState } from '../../../../services/user.service';
import { useWorkflowChrome } from '../../../../components/Layout/WorkflowChromeContext';
import { permissionPolicies } from '../../../../permissions/policies';
import { REPORT_TYPES } from '../../../../consts/reportTypes';
import { AddJurisdictionContainer } from '../AddJurisdiction';

export default function Jurisdictions() {
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();

  // Automation route: /project/quaters/:id/:periodId/jurisdictions
  // Scratch route:    /project/:projectId/jurisdictions?periodId=:p
  const workflowId = params.id || params.projectId;
  const periodId = params.periodId || searchParams.get('periodId');

  const { data: project } = useGetWorkflowById({
    id: workflowId,
    queryParams: { enabled: !!workflowId }
  });

  const { data: currentUser } = useUserGroupsCurrentUserAdminState();

  const currentPeriod = project?.periods?.find(p => p.id === periodId);
  const isLockedPeriod = !!currentPeriod?.is_locked;

  // Conversion's primary workflow IS Jurisdictions — a previous
  // `!isConversionReportType &&` short-circuit forced `canManageWorkflow`
  // to false for every Conversion user (including the assigned owner) and
  // surfaced a "You don't have permission" tooltip on Add Jurisdiction.
  // Conversion now follows the same checkAccess gate as Annex IV /
  // Form PF / Custom.
  const canManageWorkflow =
    currentUser && project
      ? permissionPolicies.checkAccess({
          currentUserRole: currentUser?.role,
          currentUserId: currentUser?.azure_object_id,
          assignedTo: project?.assigned_to
        })
      : true;

  // On close / subsequent save (no redirect from useJurisdiction), advance
  // to the next workflow step. Both scratch and automation have the same
  // stepper order at this point (AIFs → Jurisdictions → Source Files), so
  // Source Files is the correct forward destination in both flows. Previous
  // version bounced scratch back to AddAIFs, which made the initial-setup
  // wizard feel like it looped instead of progressing.
  // First-create redirects are still handled inside useJurisdiction
  // (Extraction for non-Conversion, Report for Conversion).
  const handleClose = () => {
    navigate(`/project/quaters/${workflowId}/${periodId}/upload-files`);
  };

  const { setLabels, clearLabels } = useWorkflowChrome();
  useEffect(() => {
    setLabels({
      projectLabel: project?.name,
      periodLabel:
        currentPeriod?.year && currentPeriod?.period
          ? `${currentPeriod.year}, ${currentPeriod.period}`
          : undefined,
      workflowId,
      periodId,
      activeStageKey: 'jurisdictions'
    });
    return () => clearLabels();
  }, [
    project?.name,
    currentPeriod?.year,
    currentPeriod?.period,
    workflowId,
    periodId,
    setLabels,
    clearLabels
  ]);

  if (!periodId || !workflowId) return null;

  return (
    <AddJurisdictionContainer
      isOpen={true}
      selectedPeriodId={periodId}
      workflowId={workflowId}
      refetch={() => {}}
      reportType={project?.report_type || REPORT_TYPES.ANNEX_IV}
      projectName={project?.name}
      onClose={handleClose}
      canManageWorkflow={canManageWorkflow}
      isLockedPeriod={isLockedPeriod}
      isRedirectToExtraction={true}
    />
  );
}
